import { useRef, useState } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { CardPaymentClaim, Transaction } from '@asia/shared';
import { fetchTxs, type TxPage } from '../lib/tron';
import { mergeTxsPage0 } from '../lib/tx-cache';
import { useSession } from './useSession';
import { getWalletSeedOrThrow } from '../lib/wallet';
import { useWallets } from '../store/wallets';
import {
  tronAddressFromMnemonic,
  signTronTransfer,
  signTronUsdtTransfer,
} from '../lib/tron-sign';
import { rpcErrorCode } from '../lib/api';
import { createTransferApi, broadcastTransferApi, sendTrxApi } from '../lib/transfers';

export type SendStage = 'idle' | 'creating' | 'signing' | 'broadcasting';

function randomKey(): string {
  const g = globalThis.crypto;
  if (g?.randomUUID) return g.randomUUID();
  return `k${Date.now()}${Math.random().toString(16).slice(2)}`;
}

/** Stable fingerprint for one logical USDT send — reused across retries so the
 *  server returns the same plan instead of minting a conflicting draft. */
function sendFingerprint(p: {
  walletId: string;
  to: string;
  amount: string;
  cardPayment?: CardPaymentClaim;
}): string {
  const claim = p.cardPayment
    ? p.cardPayment.kind === 'issue'
      ? `issue:${p.cardPayment.templateId}`
      : `topup:${p.cardPayment.cardId}:${p.cardPayment.amount}`
    : 'plain';
  return `${p.walletId}|${p.to}|${p.amount}|${claim}`;
}

/** Prepend a just-broadcast send into the infinite txs cache. Seeds page 0 when
 *  the list was never loaded (Send unmounts the home screen's useTxs). */
function prependTx(old: InfiniteData<TxPage> | undefined, tx: Transaction): InfiniteData<TxPage> {
  if (!old || old.pages.length === 0) {
    return { pages: [{ txs: [tx], nextBefore: null }], pageParams: [undefined] };
  }
  if (old.pages[0]!.txs.some((t) => t.id === tx.id)) return old;
  const pages = old.pages.slice();
  pages[0] = { ...pages[0]!, txs: [tx, ...pages[0]!.txs] };
  return { ...old, pages };
}

/** TronGrid 429/5xx must not dump the user on a toast after one miss. */
async function withRpcRetry<T>(fn: () => Promise<T>, attempts = 4): Promise<T> {
  let last: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      last = e;
      if (rpcErrorCode(e) !== 'rpc_unavailable' || i === attempts - 1) throw e;
      await new Promise((r) => setTimeout(r, 700 * 2 ** i));
    }
  }
  throw last;
}

/** Orchestrates a gasless USDT dual-transfer send (OwnKey-style):
 *  create (no rent) → sign main [+ fee] → broadcast (server rents after sig check). */
export function useSendTransfer() {
  const { token } = useSession();
  const qc = useQueryClient();
  const [stage, setStage] = useState<SendStage>('idle');
  /** One in-flight send — double-taps share the same promise. */
  const activeRef = useRef<Promise<{ txHash: string | null; transferId: string | null }> | null>(
    null,
  );
  /** Idempotency key for the current fingerprint until success. */
  const idemRef = useRef<{ fp: string; key: string } | null>(null);

  function send(params: {
    walletId: string;
    index: number;
    address: string;
    to: string;
    amount: string;
    coin: 'USDT' | 'TRX';
    reauthToken?: string;
    cardPayment?: CardPaymentClaim;
  }): Promise<{ txHash: string | null; transferId: string | null }> {
    if (activeRef.current) return activeRef.current;
    const task = runSend(params).finally(() => {
      activeRef.current = null;
    });
    activeRef.current = task;
    return task;
  }

  async function runSend(params: {
    walletId: string;
    index: number;
    address: string;
    to: string;
    amount: string;
    coin: 'USDT' | 'TRX';
    reauthToken?: string;
    cardPayment?: CardPaymentClaim;
  }): Promise<{ txHash: string | null; transferId: string | null }> {
    const { walletId, address, to, amount, coin, reauthToken, cardPayment } = params;
    let cur: SendStage = 'creating';
    const at = (s: SendStage) => {
      cur = s;
      setStage(s);
    };
    try {
      at('creating');
      const wallet = useWallets.getState().wallets.find((w) => w.id === walletId);
      if (!token || !wallet) throw new Error('no_seed');
      const { mnemonic, index } = await getWalletSeedOrThrow(token, wallet, reauthToken).catch(() => {
        throw new Error('no_seed');
      });
      const owner = tronAddressFromMnemonic(mnemonic, index);
      if (owner !== address) throw new Error('prepared_payload_mismatch');

      let txHash: string | null;
      let transferId: string | null = null;
      if (coin === 'TRX') {
        at('signing');
        const { signedTransaction } = await signTronTransfer({ mnemonic, index, to, amount });
        at('broadcasting');
        const res = await sendTrxApi({
          walletId,
          to,
          amount,
          idempotencyKey: randomKey(),
          signedTransaction,
          reauthToken,
        });
        txHash = res.transfer.txHash ?? null;
        transferId = res.transfer.id;
        if (res.transfer.status === 'failed') {
          throw new Error(res.transfer.failureReason ?? 'transfer_failed');
        }
      } else {
        const fp = sendFingerprint({ walletId, to, amount, cardPayment });
        const idempotencyKey =
          idemRef.current?.fp === fp ? idemRef.current.key : randomKey();
        idemRef.current = { fp, key: idempotencyKey };

        const plan = await withRpcRetry(() =>
          createTransferApi({
            walletId,
            token: 'USDT',
            to,
            amount,
            idempotencyKey,
            reauthToken,
            cardPayment,
          }),
        );
        if (owner !== plan.preparedOwner) throw new Error('prepared_payload_mismatch');

        // Lost HTTP after broadcast: same idempotency returns the row with txHash —
        // do not re-sign (new payload → transfer_payload_mismatch).
        if (plan.transfer.txHash) {
          txHash = plan.transfer.txHash;
          transferId = plan.transfer.id;
          if (plan.transfer.status === 'failed') {
            throw new Error(plan.transfer.failureReason ?? 'transfer_failed');
          }
          idemRef.current = null;
        } else if (plan.signedReady) {
          // Server already has live signed payloads — retry broadcast without re-sign.
          at('broadcasting');
          const res = await withRpcRetry(() =>
            broadcastTransferApi(plan.transfer.id, undefined, reauthToken),
          );
          txHash = res.transfer.txHash ?? null;
          transferId = plan.transfer.id;
          if (res.transfer.status === 'failed') {
            throw new Error(res.transfer.failureReason ?? 'transfer_failed');
          }
          idemRef.current = null;
        } else {
          at('signing');
          // Sequential builds — shared TronGrid QPS 15 via API proxy.
          const main = await signTronUsdtTransfer({
            mnemonic,
            index,
            to: plan.preparedRecipient,
            amount: plan.preparedAmount,
          });
          const fee =
            plan.mode === 'client-tron-dual-transfer' && plan.feeUsd > 0
              ? await signTronUsdtTransfer({
                  mnemonic,
                  index,
                  to: plan.treasury,
                  amount: String(plan.feeUsd),
                })
              : null;

          at('broadcasting');
          const res = await withRpcRetry(() =>
            broadcastTransferApi(
              plan.transfer.id,
              main.signedTransaction,
              reauthToken,
              fee?.signedTransaction,
            ),
          );
          txHash = res.transfer.txHash ?? null;
          transferId = plan.transfer.id;
          if (res.transfer.status === 'failed') {
            throw new Error(res.transfer.failureReason ?? 'transfer_failed');
          }
          // Next intentional send gets a fresh key.
          idemRef.current = null;
        }
      }

      if (txHash) {
        const optimistic: Transaction = {
          id: txHash,
          type: 'send',
          status: 'pending',
          coin,
          chain: 'TRON',
          amount: -(Number(amount.replace(',', '.')) || 0),
          amountUsd: 0,
          date: new Date().toISOString(),
          address: to,
          explorerUrl: `https://tronscan.org/#/transaction/${txHash}`,
        };
        qc.setQueryData<InfiniteData<TxPage>>(['txs', address], (old) => prependTx(old, optimistic));

        if (token) {
          for (const ms of [6000, 15000]) {
            setTimeout(() => {
              void (async () => {
                try {
                  const page = await fetchTxs(token, address);
                  qc.setQueryData<InfiniteData<TxPage>>(['txs', address], (old) =>
                    mergeTxsPage0(old, page.txs, page.nextBefore),
                  );
                } catch {
                  /* keep optimistic */
                }
                void qc.invalidateQueries({ queryKey: ['balances', address] });
              })();
            }, ms);
          }
        }
      }
      void qc.invalidateQueries({ queryKey: ['balances', address] });
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      setStage('idle');
      return { txHash, transferId };
    } catch (e) {
      setStage('idle');
      // Keep the idempotency key only when retrying the *same* draft helps
      // (network blip / hard-block). Terminal outcomes must clear it or the
      // next create for this fingerprint forever replays the dead row.
      const code = rpcErrorCode(e) ?? (e instanceof Error ? e.message : '');
      const keepIdem =
        code === 'rpc_unavailable' ||
        code === 'timeout' ||
        code === 'ws_closed' ||
        code === 'ws_timeout' ||
        code === 'transfer_in_progress' ||
        code === 'transfer_preparing' ||
        code === 'signed_transaction_expired' ||
        (!code && (cur === 'creating' || cur === 'signing' || cur === 'broadcasting'));
      if (!keepIdem) idemRef.current = null;
      if (e && typeof e === 'object') (e as { stage?: string }).stage = cur;
      throw e;
    }
  }

  return { stage, send };
}
